import {
  Platform,
  inferMediaType,
  extractMetrics,
  readMetric,
  METRIC_KEYS,
  type FeedFilters,
  type SocialPost,
  type PlatformPost,
  type SocialPostResult,
  type PaginatedResponse,
  type PlatformMedia,
  type AccountConfiguration,
  type PlatformConfigurations,
  type AccountFeedQuery,
  type AccountFeedResponse,
} from "@/types";
import { z } from "zod";
import {
  sdkSocialAccountSchema,
  sdkSocialPostSchema,
  sdkSocialPostResultSchema,
  sdkPlatformPostSchema,
  sdkListResponseSchema,
  sdkCursorResponseSchema,
  sdkAuthUrlResponseSchema,
  sdkSuccessResponseSchema,
  type SDKMediaItem,
  type SDKSocialAccount,
  type SDKSocialPost,
  type SDKPlatformPost,
} from "@/lib/schemas";

/** API base URL — absolute in dev (CORS-enabled worker), or prod worker domain. */
const API_BASE =
  import.meta.env["VITE_API_BASE_URL"] || "https://hypesocial-post.hypelive.workers.dev";

// ============================================
// AUTH
// ============================================

function getAuthToken(): string | null {
  return localStorage.getItem("auth_token");
}

function authHeaders(): Record<string, string> {
  const token = getAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// ============================================
// QUERY PARAM BUILDERS
// ============================================

/** Build query params for GET /posts (offset pagination).
 *  Aligned with SDK SocialPostListParams: offset, limit, status, platform */
export function buildFeedQueryParams(filters: FeedFilters): URLSearchParams {
  const params = new URLSearchParams();

  params.append("offset", String(filters.offset ?? 0));
  params.append("limit", String(filters.limit ?? 20));
  params.append("status", filters.status);

  if (filters.platform) {
    params.append("platform", filters.platform);
  }

  return params;
}

/** Build query params for GET /social-account-feeds/:id (cursor pagination).
 *  Aligned with SDK SocialAccountFeedListParams. */
function buildAccountFeedQueryParams(query: AccountFeedQuery): URLSearchParams {
  const params = new URLSearchParams();
  params.append("limit", String(query.limit ?? 50));

  if (query.cursor) params.append("cursor", query.cursor);
  if (query.expand?.length) {
    for (const val of query.expand) params.append("expand", val);
  }
  if (query.socialPostIds?.length) {
    for (const val of query.socialPostIds) params.append("social_post_id", val);
  }
  if (query.externalPostIds?.length) {
    for (const val of query.externalPostIds) params.append("external_post_id", val);
  }
  if (query.platformPostIds?.length) {
    for (const val of query.platformPostIds) params.append("platform_post_id", val);
  }

  return params;
}

// ============================================
// RESPONSE TRANSFORMS — validated SDK types → frontend camelCase
// ============================================

function transformMediaItem(m: SDKMediaItem): PlatformMedia {
  return {
    url: m.url,
    thumbnailUrl: typeof m.thumbnail_url === "string" ? m.thumbnail_url : undefined,
    thumbnailTimestamp:
      typeof m.thumbnail_timestamp_ms === "number" ? m.thumbnail_timestamp_ms : null,
    type: inferMediaType(m.url),
    tags: m.tags?.map((t) => ({
      id: t.id,
      type: t.type,
      platform: t.platform,
      x: t.x,
      y: t.y,
    })),
    skipProcessing: m.skip_processing ?? false,
  };
}

function transformBackendMedia(media: SDKMediaItem[] | null): PlatformMedia[] {
  if (!Array.isArray(media)) return [];
  return media.map(transformMediaItem);
}

function derivePlatform(accounts: SDKSocialAccount[]): Platform {
  if (!accounts.length) return Platform.X;
  const normalized = accounts[0]!.platform.toLowerCase();
  if (normalized === "instagram_w_facebook") return Platform.Instagram;
  if (normalized === "tiktok_business") return Platform.TikTok;
  if (Object.values(Platform).includes(normalized as Platform)) {
    return normalized as Platform;
  }
  return Platform.X;
}

/** Transform validated SDK SocialAccount → frontend SocialAccount.
 *  Tokens (access_token, refresh_token, expiry) are intentionally stripped. */
function transformBackendAccount(a: SDKSocialAccount): import("@/types").SocialAccount {
  return {
    id: a.id,
    platform: a.platform,
    username: a.username,
    userId: a.user_id,
    profilePhotoUrl: a.profile_photo_url,
    externalId: a.external_id,
    status: a.status,
    metadata: a.metadata ?? undefined,
  };
}

function transformAccountConfigurations(
  configs: Array<{ social_account_id: string; configuration: Record<string, unknown> }> | null,
): AccountConfiguration[] {
  if (!Array.isArray(configs)) return [];
  return configs.map((c) => ({
    socialAccountId: c.social_account_id,
    configuration: c.configuration,
  }));
}

function transformPlatformConfigurations(
  raw: Record<string, unknown> | null,
): PlatformConfigurations | undefined {
  if (raw === null || typeof raw !== "object") return undefined;
  return raw as PlatformConfigurations;
}

/** Transform validated SDK SocialPost → frontend SocialPost. */
function transformBackendPost(raw: SDKSocialPost): SocialPost {
  const media = transformBackendMedia(raw.media);
  const socialAccounts = raw.social_accounts.map(transformBackendAccount);

  return {
    id: raw.id,
    externalId: raw.external_id,
    caption: raw.caption,
    content: raw.caption,
    status: raw.status,
    scheduledAt: raw.scheduled_at,
    media,
    platform: derivePlatform(raw.social_accounts),
    socialAccounts,
    platformConfigurations: transformPlatformConfigurations(raw.platform_configurations),
    accountConfigurations: transformAccountConfigurations(raw.account_configurations),
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
  };
}

/** Map upstream provider values to our Platform enum. */
function mapAccountFeedPlatform(provider: string): Platform {
  const normalized = provider.toLowerCase();
  if (normalized === "instagram_w_facebook") return Platform.Instagram;
  if (normalized === "tiktok_business") return Platform.TikTok;
  if (Object.values(Platform).includes(normalized as Platform)) {
    return normalized as Platform;
  }
  return Platform.X;
}

/** Transform validated SDK PlatformPost → frontend PlatformPost. */
function transformPlatformPost(raw: SDKPlatformPost): PlatformPost {
  const platform = mapAccountFeedPlatform(raw.platform);

  const mediaItems: PlatformMedia[] = Array.isArray(raw.media)
    ? raw.media
        .filter((m): m is Record<string, unknown> => m !== null && typeof m === "object")
        .map((m) => {
          const url = String(m["url"] ?? "");
          return {
            url,
            thumbnailUrl: typeof m["thumbnail_url"] === "string" ? m["thumbnail_url"] : undefined,
            type: inferMediaType(url),
            dimensions:
              typeof m["width"] === "number" && typeof m["height"] === "number"
                ? { width: m["width"], height: m["height"] }
                : undefined,
            thumbnailTimestamp:
              typeof m["thumbnail_timestamp_ms"] === "number" ? m["thumbnail_timestamp_ms"] : null,
            tags: undefined,
          };
        })
    : [];

  return {
    socialPostId: raw.social_post_id ?? undefined,
    socialPostResultId: raw.social_post_result_id ?? undefined,
    platformPostId: raw.platform_post_id,
    externalPostId: raw.external_post_id ?? null,
    socialAccountId: raw.social_account_id,
    platformAccountId: raw.platform_account_id,
    externalAccountId: raw.external_account_id ?? undefined,
    platform,
    caption: raw.caption,
    media: mediaItems,
    platformUrl: raw.platform_url,
    postedAt: raw.posted_at ?? undefined,
    metrics: raw.metrics ?? undefined,
    createdAt: raw.posted_at ?? "",
  };
}

// ============================================
// API CLIENT — /social-posts (scheduled posts)
// ============================================

export async function fetchSocialPosts(
  filters: FeedFilters,
): Promise<PaginatedResponse<SocialPost>> {
  const params = buildFeedQueryParams(filters);
  const response = await fetch(`${API_BASE}/api/social-posts?${params.toString()}`, {
    headers: authHeaders(),
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch social posts: ${response.status} ${response.statusText}`);
  }

  const parsed = sdkListResponseSchema(sdkSocialPostSchema).parse(await response.json());
  const items = parsed.items ?? parsed.data ?? [];
  const total = parsed.total ?? parsed.count ?? 0;

  return {
    transformedData: items.map(transformBackendPost),
    count: total,
  };
}

export async function fetchSocialPost(id: string): Promise<SocialPost> {
  const response = await fetch(`${API_BASE}/api/social-posts/${id}`, {
    headers: authHeaders(),
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch post: ${response.status} ${response.statusText}`);
  }
  const parsed = sdkSocialPostSchema.parse(await response.json());
  return transformBackendPost(parsed);
}

export async function updateSocialPost(
  id: string,
  input: {
    caption?: string;
    social_accounts?: string[];
    scheduled_at?: string | null;
    media?: Array<{ url: string; type?: string }>;
    platform_configurations?: Record<string, unknown>;
    account_configurations?: AccountConfiguration[];
    isDraft?: boolean;
  },
): Promise<SocialPost> {
  const body: Record<string, unknown> = { ...input };
  if (input.isDraft !== undefined) {
    body["is_draft"] = input.isDraft;
    delete body["isDraft"];
  }
  const response = await fetch(`${API_BASE}/api/social-posts/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const err = await response.text().catch(() => "Unknown error");
    throw new Error(`Failed to update post: ${response.status} ${err}`);
  }
  const parsed = sdkSocialPostSchema.parse(await response.json());
  return transformBackendPost(parsed);
}

export async function deleteSocialPost(id: string): Promise<{ success: boolean }> {
  const response = await fetch(`${API_BASE}/api/social-posts/${id}`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  if (!response.ok) {
    const err = await response.text().catch(() => "Unknown error");
    throw new Error(`Failed to delete post: ${response.status} ${err}`);
  }
  return sdkSuccessResponseSchema.parse(await response.json());
}

// ============================================
// API CLIENT — /social-account-feeds (live platform posts)
// ============================================

export async function fetchAccountFeed(
  accountId: string,
  query: AccountFeedQuery,
): Promise<AccountFeedResponse<PlatformPost>> {
  const params = buildAccountFeedQueryParams(query);

  const response = await fetch(
    `${API_BASE}/api/social-account-feeds/${accountId}?${params.toString()}`,
    {
      headers: authHeaders(),
    },
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch account feed: ${response.status} ${response.statusText}`);
  }

  const parsed = sdkCursorResponseSchema(sdkPlatformPostSchema).parse(await response.json());

  return {
    data: parsed.data.map(transformPlatformPost),
    meta: {
      cursor: parsed.meta.cursor,
      limit: parsed.meta.limit,
      next: parsed.meta.next,
      hasMore: parsed.meta.has_more,
    },
  };
}

// ============================================
// API CLIENT — /social-post-results
// ============================================

export async function fetchPostResults(postId: string): Promise<SocialPostResult[]> {
  const response = await fetch(`${API_BASE}/api/social-post-results?post_id=${postId}`, {
    headers: authHeaders(),
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch post results: ${response.status} ${response.statusText}`);
  }

  const raw = await response.json();
  const parsed = sdkListResponseSchema(sdkSocialPostResultSchema).parse(raw);
  const items = parsed.items ?? parsed.data ?? [];

  return items.map((r: z.infer<typeof sdkSocialPostResultSchema>) => ({
    id: r.id,
    postId: r.post_id,
    socialAccountId: r.social_account_id,
    success: r.success,
    error: r.error,
    details: r.details,
    platformData: r.platform_data ?? undefined,
    createdAt: r.created_at ?? "",
  }));
}

// ============================================
// FEED HELPERS
// ============================================

/** Populate a post's platformUrl from its social_post_results.
 *  Returns the first successful result's URL, or empty string if none. */
export function populatePlatformUrl(results: SocialPostResult[]): string {
  const success = results.find((r) => r.success && r.platformData?.url);
  return success?.platformData?.url ?? "";
}

// ============================================
// FILTER HELPERS
// ============================================

export function getActiveFilterLabel<T extends string>(
  filterId: T | undefined,
  options: readonly { id: T; label: string }[],
): string {
  return options.find((o) => o.id === filterId)?.label ?? options[0]?.label ?? "Unknown";
}

export function isValidFilter<T extends string>(
  filterId: T | undefined,
  validIds: readonly T[],
): boolean {
  return filterId ? validIds.includes(filterId) : true;
}

// ============================================
// SOCIAL ACCOUNTS
// ============================================

export async function fetchSocialAccounts(
  platform?: string,
): Promise<import("@/types").SocialAccount[]> {
  const params = new URLSearchParams();
  params.append("limit", "100");
  if (platform) params.append("platform", platform);

  const response = await fetch(`${API_BASE}/api/social-accounts?${params.toString()}`, {
    headers: authHeaders(),
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch social accounts: ${response.status} ${response.statusText}`);
  }

  const parsed = sdkListResponseSchema(sdkSocialAccountSchema).parse(await response.json());
  const items = parsed.items ?? parsed.data ?? [];

  return items.map(transformBackendAccount);
}

export async function getSocialAccountAuthUrl(
  platform: string,
  connectionType?: string,
): Promise<{ url: string }> {
  const body: Record<string, unknown> = { platform };
  // Wire connection_type into platform_data for Instagram/LinkedIn (SDK shape)
  if (connectionType && (platform === "instagram" || platform === "linkedin")) {
    body["platform_data"] = {
      [platform]: { connection_type: connectionType },
    };
  } else if (connectionType) {
    body["connection_type"] = connectionType;
  }

  const response = await fetch(`${API_BASE}/api/social-accounts/auth`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const err = await response.text().catch(() => "Unknown error");
    throw new Error(`Failed to get auth URL: ${response.status} ${err}`);
  }

  return sdkAuthUrlResponseSchema.parse(await response.json());
}

export async function disconnectSocialAccount(
  id: string,
): Promise<import("@/types").SocialAccount> {
  const response = await fetch(`${API_BASE}/api/social-accounts/${id}/disconnect`, {
    method: "POST",
    headers: authHeaders(),
  });
  if (!response.ok) {
    const err = await response.text().catch(() => "Unknown error");
    throw new Error(`Failed to disconnect account: ${response.status} ${err}`);
  }
  const parsed = sdkSocialAccountSchema.parse(await response.json());
  return transformBackendAccount(parsed);
}

export interface CreateSocialAccountInput {
  platform: string;
  access_token?: string;
  user_id?: string;
  platform_data?: Record<string, unknown>;
}

export async function createSocialAccount(
  input: CreateSocialAccountInput,
): Promise<import("@/types").SocialAccount> {
  const response = await fetch(`${API_BASE}/api/social-accounts`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify(input),
  });
  if (!response.ok) {
    const err = await response.text().catch(() => "Unknown error");
    throw new Error(`Failed to create account: ${response.status} ${err}`);
  }
  const parsed = sdkSocialAccountSchema.parse(await response.json());
  return transformBackendAccount(parsed);
}

// ============================================
// CREATE POST
// ============================================

export interface CreatePostInput {
  caption: string;
  social_accounts: string[];
  scheduled_at?: string | null;
  media?: Array<{ url: string; type?: string }>;
  platform_configurations?: Record<string, unknown>;
  account_configurations?: AccountConfiguration[];
  isDraft?: boolean;
}

export async function createSocialPost(input: CreatePostInput): Promise<SocialPost> {
  const response = await fetch(`${API_BASE}/api/social-posts`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify({
      caption: input.caption,
      social_accounts: input.social_accounts,
      scheduled_at: input.scheduled_at,
      media: input.media,
      platform_configurations: input.platform_configurations,
      account_configurations: input.account_configurations,
      is_draft: input.isDraft,
    }),
  });

  if (!response.ok) {
    const err = await response.text().catch(() => "Unknown error");
    throw new Error(`Failed to create post: ${response.status} ${err}`);
  }

  const parsed = sdkSocialPostSchema.parse(await response.json());
  return transformBackendPost(parsed);
}

// ============================================
// METRICS HELPERS (re-exported from types for convenience)
// ============================================

export { extractMetrics, readMetric, METRIC_KEYS };
